// Wat useEnsureAdressLocation met de `Location` van een huishouden doet: de
// opgeslagen locatie hergebruiken, opnieuw opzoeken, en wat er dan weg moet.
// Een eigen bestand omdat de hook dit niet testbaar kan houden (Firebase-hooks
// testen we niet), en de regels hier precies zijn waar het misging.
//
// WAAROM `GeocodedFrom`. De locatie onthoudt uit WELKE invoer hij kwam. Zonder
// dat vergeleek de hook de invoer met wat PDOK teruggaf, en die verschillen
// soms terwijl alles klopt: het pand heeft alleen 12A, de gebruiker typte '12',
// en dan werd het huishouden bij elk bezoek opnieuw opgezocht en weggeschreven.
// Bij 'mislukt' en 'woning onbekend' gold dat altijd (geen coördinaten).
//
// WAAROM 'unit-unknown'. Keuze van de user (2026-10-02): staan er meerdere
// woningen op het nummer en is er geen (bestaande) toevoeging, dan is het
// GEBOUW wel bekend. Kaart en buurtcijfers blijven; BAG-id en HouseholdKey
// niet, want liever geen sleutel dan die van de buren.
// AI-CONTEXT: See docs/features/postcode-huisnummer-controle.md
// AI-CONTEXT: See src/helpers/adressHelpers/householdKey.js ('WAAROM LIEVER GEEN SLEUTEL DAN EEN GOK')

import { buildLocationFromPdokDoc } from '../pdokLocation'
import buildHouseholdKey from '../adressHelpers/householdKey'
import { normalizePostcode, splitHuisnummer } from './verifyAddressMatch'
import readHouseholdField from '../adressHelpers/householdFields'

/**
 * @typedef {object} AdressInput
 * @property {string} postcode
 * @property {string} huisnummer - Mag de toevoeging bevatten ('12a')
 * @property {string} [toevoeging]
 */

/**
 * De invoer waaruit een locatie kwam, genormaliseerd: '7201 ab' + '12b' en
 * '7201AB' + '12' + '-B' zijn dezelfde.
 * @param {AdressInput} input
 * @returns {string} 'PC|nummer|TOEVOEGING', of '' zonder nummer
 */
const geocodeInputKey = ({ postcode, huisnummer, toevoeging }) => {
    const { nummer, toevoeging: t } = splitHuisnummer(huisnummer, toevoeging)
    if (nummer === null) return ''
    return `${normalizePostcode(postcode)}|${nummer}|${t}`
}

const hasPoint = (location) => Number.isFinite(location?.Lat) && Number.isFinite(location?.Lng)

/**
 * @param {object|null} existing - `Adresses.Location`
 * @param {AdressInput} input
 * @returns {{reuse: boolean, write: boolean}} `reuse`: opgeslagen locatie is goed, niet opzoeken. Anders opzoeken, en alleen wegschrijven als `write`.
 */
const canReuseLocation = (existing, input) => {
    if (!existing) return { reuse: false, write: true }

    // Niets ingevuld om mee te vergelijken → vertrouw een bruikbare locatie.
    if (!input.postcode || !input.huisnummer) return { reuse: hasPoint(existing), write: !hasPoint(existing) }

    const key = geocodeInputKey(input)
    if (existing.GeocodedFrom) {
        if (existing.GeocodedFrom !== key) return { reuse: false, write: true }
        // Zelfde invoer. Een gevonden woning is af; bij 'mislukt' of 'woning
        // onbekend' is er een melding te tonen, en die vraagt een opzoeking —
        // maar wegschrijven verandert niets.
        if (existing.GeocodeStatus === 'ok' && hasPoint(existing)) return { reuse: true, write: false }
        return { reuse: false, write: false }
    }

    // Oude locatie, van vóór GeocodedFrom: vergelijk op nummer en toevoeging.
    if (existing.GeocodeStatus !== 'failed' && hasPoint(existing)) {
        const stored = geocodeInputKey({
            postcode: existing.Postcode,
            huisnummer: existing.Huisnummer,
            toevoeging: existing.HuisnummerToevoeging,
        })
        if (stored === key) return { reuse: true, write: false }
    }
    return { reuse: false, write: true }
}

/**
 * Wat er na een opzoeking op het Adresses-document komt (zonder GeocodedAt; die
 * zet de aanroeper erbij).
 * @param {import('./verifyAddressMatch').AddressCheck} check
 * @param {AdressInput} input
 * @returns {{status: 'ok'|'unit-unknown'|'failed', Location: object, HouseholdKey: string}}
 */
const buildLocationWrite = (check, input) => {
    const GeocodedFrom = geocodeInputKey(input)
    const typed = { postcode: input.postcode, huisnummer: input.huisnummer, toevoeging: input.toevoeging }

    if (check.verdict === 'match' && check.found) {
        const Location = { ...buildLocationFromPdokDoc(check.found, typed), GeocodedFrom }
        // In DEZELFDE write als de Location waaruit hij is afgeleid.
        return { status: 'ok', Location, HouseholdKey: buildHouseholdKey(Location) }
    }

    if (check.building) {
        const { HuisnummerToevoeging: _gebouw, ...gebouw } = buildLocationFromPdokDoc(check.building, typed)
        const Location = {
            ...gebouw,
            // Wat de gebruiker typte, niet de woning van de buren.
            HuisnummerToevoeging: splitHuisnummer(input.huisnummer, input.toevoeging).toevoeging,
            BAGid: '',
            GeocodeStatus: 'unit-unknown',
            GeocodedFrom,
        }
        return { status: 'unit-unknown', Location, HouseholdKey: '' }
    }

    // Expliciet leegmaken: een blijven staan sleutel wijst naar een woning die
    // we niet kunnen vaststellen, en op zo'n sleutel matchen organisaties elkaar.
    return { status: 'failed', Location: { GeocodeStatus: 'failed', GeocodedFrom }, HouseholdKey: '' }
}

/**
 * Het adres waarmee de locatie van een huishouden wordt opgezocht: het vaste
 * adres op het huishouden, en tot de migratie de oude gegevensregels
 * (`readHouseholdField`). Nooit `Location` zelf — dat is de UITKOMST.
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T5)
 * @param {object|null|undefined} adress - Het `Adresses`-document
 * @param {Array<{Title?: string, Value?: string}>|undefined} personaData - PersonaData van dit huishouden
 * @returns {AdressInput} Lege strings zolang er niets geladen is
 */
const adressLocationInput = (adress, personaData) => {
    const doc = adress || undefined
    return {
        postcode: String(readHouseholdField(doc, personaData, 'Postcode') ?? ''),
        huisnummer: String(readHouseholdField(doc, personaData, 'Huisnummer') ?? ''),
        toevoeging: String(readHouseholdField(doc, personaData, 'Toevoeging') ?? ''),
    }
}

export { geocodeInputKey, canReuseLocation, buildLocationWrite, adressLocationInput }
